import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, HelpCircle } from 'lucide-react';
import { ImageUpload } from '@/components/ui/image-upload';
import { useCreateProduct, useUpdateProduct, useNextProductCode } from '@/hooks/queries/useProducts';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/components/ui/toast/useToast';
import type { Product } from '@lebusiness/types';

// SIMPLIFIED VALIDATION - Only truly required fields
const productSchema = z.object({
  name: z.string().min(1, 'الاسم مطلوب / Nom requis'),
  category: z.string().min(1, 'الفئة مطلوبة / Catégorie requise'),
  unit: z.string().min(1, 'الوحدة مطلوبة / Unité requise'),
  salePrice: z.number().min(0, 'السعر يجب أن يكون موجب / Prix doit être positif'),
  stockQuantity: z.number().min(0, 'الكمية يجب أن تكون موجبة / Quantité doit être positive'),
  vat: z.number().min(0).max(100),
  // Optional fields - allow undefined or 0
  code: z.string().optional(),
  barcode: z.string().optional(),
  purchasePrice: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
    z.number().min(0).optional()
  ),
  wholesalePrice: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
    z.number().min(0).optional()
  ),
  minStockLevel: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
    z.number().min(0).optional()
  ),
  maxStockLevel: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? undefined : Number(val)),
    z.number().min(0).optional()
  ),
  description: z.string().optional(),
});

type ProductFormData = z.infer<typeof productSchema>;

interface ProductFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product?: Product | null;
  onSuccess?: () => void;
}

export function ProductForm({ open, onOpenChange, product, onSuccess }: ProductFormProps) {
  const { company } = useAuthStore();
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const { data: nextCode } = useNextProductCode(company?.id || '', 'PRD');

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(product?.imageUrl || null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: product
      ? {
          code: product.code,
          name: product.name,
          barcode: product.barcode || '',
          category: product.category,
          unit: product.unit,
          purchasePrice: product.purchasePrice || undefined,
          salePrice: product.salePrice,
          wholesalePrice: product.wholesalePrice || undefined,
          vat: product.vat,
          stockQuantity: product.stockQuantity,
          minStockLevel: product.minStockLevel || undefined,
          maxStockLevel: product.maxStockLevel || undefined,
          description: product.description || '',
        }
      : {
          code: nextCode || 'PRD-001',
          category: 'Autres',
          unit: 'pcs',
          salePrice: 0,
          vat: 19,
          stockQuantity: 0,
        },
  });

  useEffect(() => {
    if (nextCode && !product) {
      setValue('code', nextCode);
    }
  }, [nextCode, product, setValue]);

  // ✅ FIX: Reset form when dialog opens with product data
  useEffect(() => {
    if (open) {
      if (product) {
        // Editing - prefill all fields (support both snake_case and camelCase)
        reset({
          code: product.code,
          name: product.name,
          barcode: product.barcode || '',
          category: product.category,
          unit: product.unit,
          purchasePrice: product.purchase_price || product.purchasePrice || undefined,
          salePrice: product.sale_price || product.salePrice,
          wholesalePrice: product.wholesale_price || product.wholesalePrice || undefined,
          vat: product.vat,
          stockQuantity: product.stock_quantity || product.stockQuantity,
          minStockLevel: product.min_stock_level || product.minStockLevel || undefined,
          maxStockLevel: product.max_stock_level || product.maxStockLevel || undefined,
          description: product.description || '',
        });
        setImageUrl(product.image_url || product.imageUrl || null);
      } else {
        // New product - clear form
        reset({
          code: nextCode || 'PRD-001',
          name: '',
          barcode: '',
          category: 'Autres',
          unit: 'pcs',
          purchasePrice: undefined,
          salePrice: 0,
          wholesalePrice: undefined,
          vat: 19,
          stockQuantity: 0,
          minStockLevel: undefined,
          maxStockLevel: undefined,
          description: '',
        });
        setImageUrl(null);
      }
    }
  }, [open, product, nextCode, reset]);

  const onSubmit = async (data: ProductFormData) => {
    if (!company?.id) {
      toast.error('خطأ / Erreur', 'معرف الشركة مفقود / ID société manquant');
      return;
    }

    console.log('Form data:', data); // Debug log

    try {
      const productData = {
        company_id: company.id,
        code: data.code || nextCode || `PRD-${Date.now()}`,
        name: data.name,
        barcode: data.barcode || null,
        category: data.category,
        unit: data.unit,
        purchase_price: data.purchasePrice ?? 0,
        sale_price: data.salePrice,
        wholesale_price: data.wholesalePrice ?? null,
        vat: data.vat,
        stock_quantity: data.stockQuantity,
        min_stock_level: data.minStockLevel || null,
        max_stock_level: data.maxStockLevel || null,
        description: data.description || null,
        image_url: imageUrl,
        is_active: true,
      };

      console.log('Submitting:', productData); // Debug log

      if (product) {
        await updateProduct.mutateAsync({ id: product.id, ...productData });
        toast.success(
          'تم التحديث بنجاح / Modifié avec succès',
          'تم تحديث المنتج / Le produit a été modifié'
        );
      } else {
        await createProduct.mutateAsync(productData);
        toast.success(
          'تم الإضافة بنجاح / Ajouté avec succès',
          'تم إضافة المنتج / Le produit a été ajouté'
        );
      }

      onSuccess?.();
      onOpenChange(false);
      reset();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(
        'خطأ / Erreur',
        error instanceof Error ? error.message : 'فشل حفظ المنتج / Échec de l\'enregistrement'
      );
    }
  };

  const categories = [
    'Électronique',
    'Fournitures',
    'Mobilier',
    'Vêtements',
    'Alimentation',
    'Construction',
    'Autres',
  ];

  const units = [
    { value: 'pcs', label: 'قطعة / Pièce' },
    { value: 'kg', label: 'كغ / Kilogramme' },
    { value: 'm', label: 'متر / Mètre' },
    { value: 'l', label: 'لتر / Litre' },
    { value: 'box', label: 'صندوق / Boîte' },
    { value: 'pack', label: 'حزمة / Pack' },
    { value: 'ream', label: 'رزمة / Rame' },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {product
              ? 'تعديل المنتج / Modifier le produit'
              : 'منتج جديد / Nouveau produit'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* BASIC INFO - Required */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">
              معلومات أساسية / Informations de base *
            </h3>

            <div className="space-y-2">
              <Label htmlFor="name">
                الاسم / Nom * 
                <span className="text-xs text-gray-500 ml-2">
                  (اسم المنتج / Nom du produit)
                </span>
              </Label>
              <Input
                id="name"
                {...register('name')}
                placeholder="مثال: لابتوب Dell / Ex: Laptop Dell"
              />
              {errors.name && (
                <p className="text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">الفئة / Catégorie *</Label>
                <Select
                  value={watch('category')}
                  onValueChange={(value) => setValue('category', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.category && (
                  <p className="text-sm text-red-600">{errors.category.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="unit">الوحدة / Unité *</Label>
                <Select
                  value={watch('unit')}
                  onValueChange={(value) => setValue('unit', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map((unit) => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.unit && (
                  <p className="text-sm text-red-600">{errors.unit.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="salePrice">
                  سعر البيع / Prix de vente * (DA)
                  <span className="text-xs text-gray-500 ml-2">
                    (السعر للعملاء / Prix pour clients)
                  </span>
                </Label>
                <Input
                  id="salePrice"
                  type="number"
                  step="0.01"
                  {...register('salePrice', { valueAsNumber: true })}
                  placeholder="0.00"
                />
                {errors.salePrice && (
                  <p className="text-sm text-red-600">{errors.salePrice.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="stockQuantity">
                  الكمية / Stock *
                  <span className="text-xs text-gray-500 ml-2">
                    (الكمية المتوفرة / Quantité disponible)
                  </span>
                </Label>
                <Input
                  id="stockQuantity"
                  type="number"
                  step="0.001"
                  {...register('stockQuantity', { valueAsNumber: true })}
                  placeholder="0"
                />
                {errors.stockQuantity && (
                  <p className="text-sm text-red-600">{errors.stockQuantity.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="vat">TVA *</Label>
              <Select
                value={watch('vat')?.toString()}
                onValueChange={(value) => setValue('vat', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0% (Exonéré)</SelectItem>
                  <SelectItem value="9">9% (Taux réduit)</SelectItem>
                  <SelectItem value="19">19% (Taux normal)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ADVANCED / OPTIONAL */}
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
            >
              <HelpCircle className="w-4 h-4" />
              {showAdvanced ? 'إخفاء / Masquer' : 'إظهار / Afficher'} الحقول الاختيارية / champs optionnels
            </button>

            {showAdvanced && (
              <div className="space-y-4 border-t pt-4">
                <h3 className="text-sm font-semibold text-gray-700">
                  معلومات إضافية / Informations supplémentaires (اختياري / Optionnel)
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="code">
                      الرمز / Code
                      <span className="text-xs text-gray-500 ml-2">
                        (يتم إنشاؤه تلقائياً / Auto-généré)
                      </span>
                    </Label>
                    <Input
                      id="code"
                      {...register('code')}
                      placeholder="PRD-001"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="barcode">
                      الباركود / Code-barres
                      <span className="text-xs text-gray-500 ml-2">
                        (للماسح / Pour scanner)
                      </span>
                    </Label>
                    <Input
                      id="barcode"
                      {...register('barcode')}
                      placeholder="1234567890"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="purchasePrice">
                      سعر الشراء / Prix d'achat (DA)
                      <span className="text-xs text-gray-500 block">
                        (ما دفعته / Ce que vous payez)
                      </span>
                    </Label>
                    <Input
                      id="purchasePrice"
                      type="number"
                      step="0.01"
                      {...register('purchasePrice', { valueAsNumber: true })}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="wholesalePrice">
                      سعر الجملة / Prix gros (DA)
                      <span className="text-xs text-gray-500 block">
                        (للكميات الكبيرة / Pour quantités)
                      </span>
                    </Label>
                    <Input
                      id="wholesalePrice"
                      type="number"
                      step="0.01"
                      {...register('wholesalePrice', { valueAsNumber: true })}
                      placeholder="0.00"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="minStockLevel">
                      الحد الأدنى / Min stock
                      <span className="text-xs text-gray-500 block">
                        (للتنبيه / Pour alerte)
                      </span>
                    </Label>
                    <Input
                      id="minStockLevel"
                      type="number"
                      step="0.001"
                      {...register('minStockLevel', { valueAsNumber: true })}
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">
                    الوصف / Description
                  </Label>
                  <Textarea
                    id="description"
                    {...register('description')}
                    placeholder="وصف المنتج... / Description du produit..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>
                    الصورة / Image
                    <span className="text-xs text-gray-500 ml-2">
                      (اختياري / Optionnel)
                    </span>
                  </Label>
                  <ImageUpload value={imageUrl} onChange={setImageUrl} />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              إلغاء / Annuler
            </Button>
            <Button
              type="submit"
              disabled={createProduct.isPending || updateProduct.isPending}
              className="bg-gradient-to-r from-emerald-500 to-teal-600"
            >
              {(createProduct.isPending || updateProduct.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {product ? 'حفظ / Enregistrer' : 'إضافة / Ajouter'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
